import React from 'react';
import PropTypes from 'prop-types';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useRowSelect,
  useFlexLayout,
} from 'react-table';
import { withTranslation } from 'react-i18next';
import {
  LoadingCell,
  NoResultCell,
  GlobalFilter,
  EditableCell,
} from './helper';
import { renderSelectionColumn } from './SelectionColumn';
import Pagination from './Pagination';
import ContextMenu from './ContextMenu';

const Table = ({ selectRow, hasPagination, ...props }) => {
  const data = React.useMemo(() => props.data, []);
  const columns = React.useMemo(() => props.columns, []);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize, globalFilter, selectedRowIds },
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageSize: 25, pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      selectRow && renderSelectionColumn(hooks);
    },
    useFlexLayout,
  );
  const defaultColumn = {
    Cell: EditableCell,
  };
  const [ctxMenu, setCtxMenu] = React.useState({
    openCtxMenu: false,
    ctxMenuCoord: null,
    ctxMenuRow: null,
    ctxMenuIdx: null,
  });
  const toggleContextMenu = (e, row, rowIndex, cell) => {
    let update = { openCtxMenu: !ctxMenu.openCtxMenu };
    if (e) {
      e.preventDefault();
      update = Object.assign(update, {
        ctxMenuCoord: { x: e.clientX, y: e.clientY },
      });
    }
    setCtxMenu({
      ...update,
      ctxMenuRow: row,
      ctxMenuIdx: rowIndex,
    });
  };
  React.useEffect(() => {
    const { onSelect, onSelectAll } = selectRow;
    if (onSelect) {
      selectRow.onSelect(selectedFlatRows);
    }
    if (onSelectAll) {
      selectRow.onSelect(selectedFlatRows);
    }
  }, [selectedRowIds]);
  function renderTbody() {
    if (props.loading) {
      return <LoadingCell />;
    } else if (page.length) {
      return page.map((row, i) => {
        prepareRow(row);
        return (
          <tr {...row.getRowProps()}>
            {row.cells.map((cell) => {
              return (
                <td
                  {...cell.getCellProps()}
                  onContextMenu={(e) =>
                    toggleContextMenu(e, row.values, row.index, cell.value)
                  }
                >
                  {cell.render('Cell')}
                </td>
              );
            })}
          </tr>
        );
      });
    } else {
      return <NoResultCell />;
    }
  }
  //prettier-ignore
  const { openCtxMenu, ctxMenuCoord, ctxMenuRow, ctxMenuIdx } = ctxMenu;
  return (
    <div className="hgs-datagrid">
      {props.ctxMenuOptions ? (
        <ContextMenu
          options={props.ctxMenuOptions}
          isOpen={openCtxMenu}
          toggleFunc={toggleContextMenu}
          coord={ctxMenuCoord}
          actionData={ctxMenuRow}
          actionIndex={ctxMenuIdx}
        />
      ) : null}
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <hr />
      <table
        className="table table-bordered table-hover table-responsive table-sm d-flex flex-column"
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} className="table-header">
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}{' '}
                  {/* Add a sort direction indicator */}
                  <span className="md-icon sort-icon">
                    {column.isSorted
                      ? column.isSortedDesc
                        ? 'arrow_downward'
                        : 'arrow_upward'
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>{renderTbody()}</tbody>
      </table>
      {hasPagination && (
        <Pagination
          pageSize={pageSize}
          pageIndex={pageIndex}
          page={page}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageOptions={pageOptions}
          pageCount={pageCount}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
};
Table.defaultProps = {
  hasPagination: false,
};
export default withTranslation('app')(Table);
